import { useQuery } from '@tanstack/react-query';
import { getMyPermissions, type MyPermissions, type Permission } from '@/api/endpoints/permissions';

/**
 * SU-6 (W7, ADR-0049): the front side of permissions_v2. Reads
 * GET /api/permissions/me once per session (5 min), and answers can(p).
 *
 * While the organisation's flag is off, or until the answer arrives, can()
 * keeps today's role rules (the server sends the role defaults), so nothing
 * on screen changes. The server stays the authority: this only hides what
 * would answer 403 PERMISSION_DENIED. The role helpers of the former
 * usePermissions (hooks/usePermissions.ts) are untouched.
 */
export const MY_PERMISSIONS_KEY = ['permissions', 'me'] as const;

export function usePermissionMatrix() {
  const query = useQuery<MyPermissions>({ queryKey: MY_PERMISSIONS_KEY, queryFn: getMyPermissions, staleTime: 5 * 60_000 });
  const data = query.data;
  return {
    loading: query.isLoading,
    enforced: data?.enforced ?? false,
    flags: data?.flags ?? { permissions_v2: false, supervision_live: false },
    isSuperAdmin: data?.is_super_admin ?? false,
    role: data?.role ?? null,
    /** Unknown (not loaded, or the read failed) → true: the server decides. */
    // A malformed answer (no `permissions` map) counts as unknown: never throw in render.
    can: (permission: Permission): boolean => data?.permissions?.[permission] !== false,
  };
}
